// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { xIsAuthenticated } from "@tps/App/Store/Selectors/GeneralSelectors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    city: state.components.contactUsForm.fieldValues["city"],
    // xng-change
    isAuthenticated: xIsAuthenticated(state),
    billTo: getCurrentBillToState(state).value,
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CityStyles {
    cityTextField?: TextFieldPresentationProps;
}

export const cityStyles: CityStyles = {};

const styles = cityStyles;

const City: React.FC<Props> = ({ fields, city, setFieldValue, isAuthenticated, billTo }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [cityErrorMessage, setCityErrorMessage] = React.useState("");

    // xng-change
    React.useEffect(() => {
        if (isAuthenticated && billTo && (billTo.city || billTo.state)) {
            const customerLocation = [billTo.city, billTo.state?.name].filter(Boolean).join(", ");
            setFieldValue({ key: "city", value: customerLocation });
        }
    }, [isAuthenticated, billTo?.city , setFieldValue]);

    React.useEffect(() => {
        contactFormContext.validators.city = validateCity;
        if (typeof city !== "undefined") {
            validateCity();
        }
    }, [city]);

    React.useEffect(() => {
        contactFormContext.validators.city = validateCity;
    }, [isRequired]);

    const validateCity = () => {
        const errorMessage = isRequired && !city ? (siteMessage("Field_Required", label) as string) : "";
        setCityErrorMessage(errorMessage);
        return !errorMessage;
    };

    const cityChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "city", value: event.target.value });
    };

    return (
        <TextField
            {...styles.cityTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={city || ""}
            error={cityErrorMessage}
            onChange={cityChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(City),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "City",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter City",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
